import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, Trophy } from "lucide-react";

/**
 * This month, as one bar a child can read in a second.
 *
 * The tutor's ask (1 Nov 2026): somewhere on their own portal that shows how
 * far through the month they are, so missing a day feels like something.
 *
 * Three deliberate choices, all of which make it less exciting and more
 * honest.
 *
 * **It shows only this child.** No rank, no class average, nobody else's
 * name - the rule the rest of the portal already follows. The rungs are
 * absolute, so five children can all be Gold and none of them learns
 * anything about the other four.
 *
 * **The headline is days, not a percentage.** "22 of 30" is a thing a
 * fourteen-year-old can picture; 73% is a thing they argue with. The
 * percentage is there because the rungs need one, in small type.
 *
 * **The sentence under the bar names the next rung and what it costs.** "Two
 * more days and you reach Silver" is actionable this week. A bar on its own
 * tells a child who has missed a week that the month is lost, which is the
 * behaviour this is meant to prevent.
 *
 * Nothing here mentions the coupon. Who gets that is the tutor's decision,
 * made from a different screen, and a child who thinks the bar buys a voucher
 * has been promised something no code can keep.
 */

const TONE = {
  Bronze: { bar: "bg-amber-600", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  Silver: { bar: "bg-slate-400", chip: "bg-slate-50 text-slate-700 border-slate-300" },
  Gold: { bar: "bg-yellow-500", chip: "bg-yellow-50 text-yellow-800 border-yellow-300" },
  Platinum: { bar: "bg-emerald-600", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};
const STARTING = { bar: "bg-sky-500", chip: "bg-sky-50 text-sky-700 border-sky-200" };

function monthName(key) {
  if (!key) return "This month";
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

export default function MonthBar() {
  const [d, setD] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api.get("/progress/month")
      .then((r) => { setD(r.data); setState("ok"); })
      .catch(() => setState("failed"));
  }, []);

  if (state === "loading") {
    return (
      <div className="bg-white rounded-xl border border-slate-200 py-10 flex justify-center mb-8">
        <Loader2 size={20} className="animate-spin text-slate-400" />
      </div>
    );
  }
  if (state === "failed") return null;

  const s = d?.summary || {};
  // No worksheets planned this month is not the same as none done, and a bar
  // at 0% would say the wrong one. Say nothing instead.
  if (!s.target) return null;

  const pct = s.percent ?? 0;
  const tone = TONE[s.stage] || STARTING;
  const stages = d?.stages || [];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8" data-testid="month-bar">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-serif text-xl brand-navy">{monthName(d.month)}</h3>
          <p className="text-sm text-slate-500">
            <span className="font-semibold text-slate-800" data-testid="month-done">{s.done}</span>
            {" of "}{s.target} days finished
          </p>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5
                          rounded-full border ${tone.chip}`} data-testid="month-stage">
          <Trophy size={14} />
          {s.stage || "Getting started"}
        </span>
      </div>

      {/* The bar, with the rungs marked on it so the next one is a place on a
          line rather than a number in a sentence. */}
      <div className="relative h-4 rounded-full bg-slate-100 overflow-hidden mb-1">
        <div className={`h-full ${tone.bar} transition-all duration-700 rounded-full`}
             style={{ width: `${Math.min(100, pct)}%` }} />
        {stages.filter((g) => g.at_percent < 100).map((g) => (
          <span key={g.name} className="absolute top-0 h-full w-px bg-white/80"
                style={{ left: `${g.at_percent}%` }} />
        ))}
      </div>
      <div className="relative h-5 mb-4 hidden sm:block">
        {stages.map((g) => (
          <span key={g.name}
                className={`absolute -translate-x-1/2 text-[11px] ${
                  pct >= g.at_percent ? "text-slate-700 font-semibold" : "text-slate-400"}`}
                style={{ left: `${g.at_percent}%` }}>
            {g.name}
          </span>
        ))}
      </div>

      <p className="text-sm text-slate-600" data-testid="month-next">
        {s.next
          ? <>You are <span className="font-semibold">{pct}%</span> through.{" "}
              <span className="font-semibold text-slate-800">
                {s.days_to_next === 1 ? "One more day" : `${s.days_to_next} more days`}
              </span>{" "}and you reach {s.next.name}.</>
          : s.to_come
            ? <>Everything set so far is finished. Keep it up.</>
            : <>Every single day finished. That is the whole month.</>}
      </p>

      {/* Days planned but not unlocked yet. Said plainly so a full bar in
          week one reads as "up to date" rather than "the month is over" -
          and so nothing that has not been set can look like something
          missed. */}
      {s.to_come > 0 && (
        <p className="text-xs text-slate-400 mt-1" data-testid="month-to-come">
          {s.to_come} more {s.to_come === 1 ? "day is" : "days are"} still to come this month.
        </p>
      )}

      <Unfinished days={d.days || []} />
    </div>
  );
}

/**
 * What is still open, grouped by the reason.
 *
 * A bar that says "22 of 30" and cannot say which eight is a bar nobody
 * trusts twice - and the reasons are not interchangeable. "You never marked
 * it" is a two-minute fix the child can do tonight; "it closed" cannot be
 * fixed at all and is only there so nobody thinks the system lost it.
 */
function Unfinished({ days }) {
  const open = days.filter((x) => !x.done);
  if (!open.length) return null;

  const groups = {};
  for (const x of open) {
    const label = /not marked/.test(x.reason) ? "waiting for your marking"
      : x.reason === "not started" ? "not started yet"
      : x.reason === "not handed in" ? "started but not handed in"
      : x.reason === "nothing attempted" ? "nothing attempted"
      : "closed";
    (groups[label] = groups[label] || []).push(x);
  }
  // Fixable first: the whole point is to show the child what is still worth
  // doing tonight before showing them what is gone.
  const order = ["waiting for your marking", "started but not handed in",
                 "not started yet", "nothing attempted", "closed"];

  return (
    <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-2">
      {order.filter((k) => groups[k]).map((k) => (
        <span key={k}
              className={`text-xs px-2.5 py-1 rounded-full border ${
                k === "closed"
                  ? "bg-slate-50 text-slate-500 border-slate-200"
                  : "bg-amber-50 text-amber-800 border-amber-200"}`}>
          {groups[k].length} {k}
        </span>
      ))}
    </div>
  );
}
